import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Power, PowerOff, ChevronDown, ChevronUp, KeyRound } from 'lucide-react';
import Modal from '../components/Modal';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import BotonAyuda from '../components/BotonAyuda';

const SECCIONES = [
  { key: 'dashboard', label: 'Bancos' },
  { key: 'cuentas', label: 'Cuentas' },
  { key: 'movimientos', label: 'Movimientos' },
  { key: 'tasas_cambio', label: 'Tasas de Cambio' },
  { key: 'produccion', label: 'Producción' },
  { key: 'auditoria', label: 'Auditoría' },
  { key: 'configuracion', label: 'Configuración' }
];

const ROLES = ['sin_acceso', 'consultor', 'operador', 'contador', 'gerente', 'admin'];

const MODULOS = [
  { key: 'ventas', label: 'Ventas' },
  { key: 'productos', label: 'Productos / Inventario' },
  { key: 'proveedores', label: 'Proveedores' },
  { key: 'reporte_cxp', label: 'Cuentas por pagar' },
  { key: 'reporte_cxc', label: 'Cuentas por cobrar' },
  { key: 'reporte_libro_fiscal', label: 'Libro de Compras / Ventas' },
  { key: 'caja', label: 'Caja / POS' },
  { key: 'produccion', label: 'Producción' }
];

// Qué pantallas de Producción le tocan a cada fábrica (cada una produce distinto).
const TIPOS_PRODUCCION = [
  { value: 'extrusion', label: 'Extrusión (Extrusión + Estimaciones)' },
  { value: 'ninguno', label: 'Sin pantallas todavía' }
];

const PESTANAS = [
  { key: 'empresas', label: 'Empresas' },
  { key: 'usuarios', label: 'Usuarios' },
  { key: 'cajeros', label: 'Cajeros' }
];

const EMPRESA_VACIA = {
  nombre: '', rubro: 'restaurante', rfc_nit: '',
  prefijo_recibo: '', moneda_base: 'USD',
  iva_pct: '16', caja_correo_cierre: '', correo_tesoreria: ''
};

export default function Administracion() {
  const { refrescarSesion } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [cajeros, setCajeros] = useState([]);
  const [guardando, setGuardando] = useState(null);
  const [pestana, setPestana] = useState('empresas');
  const [usuarioExpandidoId, setUsuarioExpandidoId] = useState(null);
  const [empresaExpandidaId, setEmpresaExpandidaId] = useState(null);

  const [mostrarFormEmpresa, setMostrarFormEmpresa] = useState(false);
  const [editandoEmpresaId, setEditandoEmpresaId] = useState(null);
  const [empresaForm, setEmpresaForm] = useState(EMPRESA_VACIA);
  const [errorEmpresa, setErrorEmpresa] = useState('');

  async function cargar() {
    const [resUsuarios, resEmpresas, resCajeros] = await Promise.all([
      api.get('/admin/usuarios'),
      api.get('/admin/empresas'),
      api.get('/admin/cajeros')
    ]);
    setUsuarios(resUsuarios.data);
    setEmpresas(resEmpresas.data);
    setCajeros(resCajeros.data);
  }

  async function resetearPinCajero(cajero) {
    if (!window.confirm(`¿Resetear el PIN de "${cajero.nombre}" (${cajero.empresa_nombre})? El PIN actual dejará de funcionar.`)) return;
    setGuardando(`cajero-${cajero.id}`);
    try {
      const { data } = await api.put(`/admin/cajeros/${cajero.id}/resetear-pin`);
      alert(`Nuevo PIN de ${data.nombre} (${cajero.empresa_nombre}): ${data.pin}\n\nComunicáselo al cajero; no se puede volver a ver.`);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo resetear el PIN');
    } finally {
      setGuardando(null);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  function empezarEdicionEmpresa(empresa) {
    setEditandoEmpresaId(empresa.id);
    setEmpresaForm({
      nombre: empresa.nombre,
      rubro: empresa.rubro,
      rfc_nit: empresa.rfc_nit || '',
      prefijo_recibo: empresa.prefijo_recibo || '',
      moneda_base: empresa.moneda_base,
      iva_pct: empresa.iva_pct != null ? String(empresa.iva_pct) : '16',
      caja_correo_cierre: empresa.caja_correo_cierre || '',
      correo_tesoreria: empresa.correo_tesoreria || ''
    });
    setMostrarFormEmpresa(true);
  }

  function cancelarEmpresa() {
    setMostrarFormEmpresa(false);
    setEditandoEmpresaId(null);
    setEmpresaForm(EMPRESA_VACIA);
    setErrorEmpresa('');
  }

  async function guardarEmpresa(e) {
    e.preventDefault();
    setErrorEmpresa('');
    try {
      if (editandoEmpresaId) {
        await api.put(`/admin/empresas/${editandoEmpresaId}`, empresaForm);
      } else {
        await api.post('/admin/empresas', empresaForm);
      }
      cancelarEmpresa();
      await cargar();
      await refrescarSesion?.();
    } catch (err) {
      setErrorEmpresa(err.response?.data?.error || 'Error al guardar la empresa');
    }
  }

  async function toggleActivoEmpresa(empresa) {
    const accion = empresa.activo ? 'Inhabilitar' : 'Habilitar';
    if (!window.confirm(`¿${accion} la empresa "${empresa.nombre}"?`)) return;
    try {
      await api.put(`/admin/empresas/${empresa.id}`, { activo: !empresa.activo });
      await cargar();
      await refrescarSesion?.();
    } catch (err) {
      alert(err.response?.data?.error || `No se pudo ${accion.toLowerCase()} la empresa`);
    }
  }

  async function toggleSeccion(usuario, seccionKey) {
    const nuevoValor = !usuario.secciones[seccionKey];
    setGuardando(usuario.id);
    try {
      await api.put(`/admin/usuarios/${usuario.id}/secciones`, {
        secciones: { [seccionKey]: nuevoValor }
      });
      await cargar();
    } finally {
      setGuardando(null);
    }
  }

  async function cambiarRolEmpresa(usuario, empresaId, rol) {
    setGuardando(usuario.id);
    try {
      await api.put(`/admin/usuarios/${usuario.id}/empresas`, {
        empresas: { [empresaId]: rol }
      });
      await cargar();
    } finally {
      setGuardando(null);
    }
  }

  async function toggleModuloUsuario(usuario, empresaId, moduloKey) {
    const ocultos = usuario.modulosOcultos?.[empresaId] || [];
    const visible = !ocultos.includes(moduloKey);
    setGuardando(usuario.id);
    try {
      await api.put(`/admin/usuarios/${usuario.id}/modulos`, {
        empresa_id: empresaId,
        modulos: { [moduloKey]: !visible }
      });
      await cargar();
      await refrescarSesion?.();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el cambio');
    } finally {
      setGuardando(null);
    }
  }

  async function cambiarTipoProduccion(empresa, tipo) {
    setGuardando(`empresa-${empresa.id}`);
    try {
      await api.put(`/admin/empresas/${empresa.id}/modulos`, { modulos: { produccion_tipo: tipo } });
      await cargar();
      await refrescarSesion?.();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el tipo de producción');
    } finally {
      setGuardando(null);
    }
  }

  async function toggleModulo(empresa, moduloKey) {
    const nuevoValor = !empresa.modulos?.[moduloKey];
    setGuardando(`empresa-${empresa.id}`);
    try {
      await api.put(`/admin/empresas/${empresa.id}/modulos`, {
        modulos: { [moduloKey]: nuevoValor }
      });
      await cargar();
    } finally {
      setGuardando(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Administración de permisos<BotonAyuda /></h1>
        <p className="text-sm text-slate-500 mt-1">
          Controla qué secciones ve cada usuario y a cuáles empresas tiene acceso — sin importar la empresa activa.
        </p>
      </div>

      <div className="flex gap-1 border-b">
        {PESTANAS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPestana(p.key)}
            className={`px-3.5 py-2.5 text-sm ${pestana === p.key ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Empresas: crear, editar e inhabilitar */}
      {pestana === 'empresas' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 pb-3">
            <h2 className="font-semibold text-slate-700">Empresas</h2>
            <button
              onClick={() => setMostrarFormEmpresa(true)}
              title="Nueva empresa"
              className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>

          {mostrarFormEmpresa && (
            <Modal title={editandoEmpresaId ? 'Editando empresa' : 'Nueva empresa'} onClose={cancelarEmpresa}>
            <form onSubmit={guardarEmpresa} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                required
                placeholder="Nombre de la empresa"
                value={empresaForm.nombre}
                onChange={(e) => setEmpresaForm({ ...empresaForm, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />
              <select
                value={empresaForm.rubro}
                onChange={(e) => setEmpresaForm({ ...empresaForm, rubro: e.target.value })}
                className="border rounded-md px-3 py-2"
              >
                <option value="restaurante">Restaurante</option>
                <option value="fabrica">Fábrica</option>
                <option value="importadora">Importadora</option>
                <option value="personal">Personal</option>
                <option value="otro">Otro</option>
              </select>
              <select
                value={empresaForm.moneda_base}
                onChange={(e) => setEmpresaForm({ ...empresaForm, moneda_base: e.target.value })}
                className="border rounded-md px-3 py-2"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="MXN">MXN</option>
              </select>
              <input
                placeholder="RFC / NIT (opcional)"
                value={empresaForm.rfc_nit}
                onChange={(e) => setEmpresaForm({ ...empresaForm, rfc_nit: e.target.value })}
                className="border rounded-md px-3 py-2"
              />
              <input
                placeholder="Prefijo de recibo (opcional, ej. CAP)"
                value={empresaForm.prefijo_recibo}
                onChange={(e) => setEmpresaForm({ ...empresaForm, prefijo_recibo: e.target.value })}
                className="border rounded-md px-3 py-2"
              />

              <label className="text-sm text-slate-600 flex flex-col gap-1">
                IVA % (Caja / POS)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={empresaForm.iva_pct}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, iva_pct: e.target.value })}
                  className="border rounded-md px-3 py-2"
                />
              </label>

              <label className="text-sm text-slate-600 flex flex-col gap-1">
                Correo para el cierre de caja (opcional)
                <input
                  type="email"
                  placeholder="cierre@ejemplo.com"
                  value={empresaForm.caja_correo_cierre}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, caja_correo_cierre: e.target.value })}
                  className="border rounded-md px-3 py-2"
                />
              </label>

              <label className="text-sm text-slate-600 flex flex-col gap-1">
                Correo de tesorería (opcional)
                <input
                  type="email"
                  placeholder="tesoreria@ejemplo.com"
                  value={empresaForm.correo_tesoreria}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, correo_tesoreria: e.target.value })}
                  className="border rounded-md px-3 py-2"
                />
                <span className="text-xs text-slate-400 font-normal">Destino de la agenda de pagos (Cuentas por Pagar) al enviarla por correo.</span>
              </label>

              {errorEmpresa && <p className="text-red-600 text-sm sm:col-span-2">{errorEmpresa}</p>}

              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
                {editandoEmpresaId ? 'Guardar cambios' : 'Guardar'}
              </button>
            </form>
            </Modal>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Nombre</th>
                  <th className="p-3">Rubro</th>
                  <th className="p-3">Moneda</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((e) => (
                  <React.Fragment key={e.id}>
                    <tr
                      onClick={() => setEmpresaExpandidaId(empresaExpandidaId === e.id ? null : e.id)}
                      className="border-t hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <td className="p-3 font-medium text-slate-700 whitespace-nowrap">{e.nombre}</td>
                      <td className="p-3 capitalize whitespace-nowrap">{e.rubro}</td>
                      <td className="p-3">{e.moneda_base}</td>
                      <td className="p-3">
                        <span
                          className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                            e.activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {e.activo ? 'Activa' : 'Inhabilitada'}
                        </span>
                      </td>
                      <td className="p-3 text-right text-slate-400 w-10">
                        {empresaExpandidaId === e.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </td>
                    </tr>
                    {empresaExpandidaId === e.id && (
                      <tr className="border-t bg-slate-50">
                        <td colSpan={5} className="p-4">
                          <div className="flex items-center gap-3 mb-4">
                            <button
                              onClick={() => empezarEdicionEmpresa(e)}
                              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-primary-600 transition-colors"
                            >
                              <Pencil size={14} /> Editar
                            </button>
                            <button
                              onClick={() => toggleActivoEmpresa(e)}
                              className={`inline-flex items-center gap-1.5 text-sm transition-colors ${
                                e.activo ? 'text-slate-600 hover:text-red-600' : 'text-slate-600 hover:text-green-600'
                              }`}
                            >
                              {e.activo ? <PowerOff size={14} /> : <Power size={14} />}
                              {e.activo ? 'Inhabilitar' : 'Habilitar'}
                            </button>
                          </div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                            Módulos habilitados
                          </p>
                          <div className="space-y-2">
                            {MODULOS.map((m) => (
                              <React.Fragment key={m.key}>
                                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!e.modulos?.[m.key]}
                                    disabled={guardando === `empresa-${e.id}`}
                                    onChange={() => toggleModulo(e, m.key)}
                                    className="w-4 h-4 accent-primary-600 cursor-pointer"
                                  />
                                  {m.label}
                                </label>
                                {m.key === 'produccion' && e.modulos?.produccion && (
                                  <div className="flex items-center gap-2 pl-6 text-xs text-slate-500">
                                    Tipo de producción
                                    <select
                                      value={e.modulos?.produccion_tipo || 'extrusion'}
                                      disabled={guardando === `empresa-${e.id}`}
                                      onChange={(ev) => cambiarTipoProduccion(e, ev.target.value)}
                                      className="border rounded-md px-2 py-1 text-xs"
                                    >
                                      {TIPOS_PRODUCCION.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {empresas.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-slate-400">
                      No hay empresas creadas todavía.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usuarios: click en una fila despliega sus permisos (secciones + rol por empresa) */}
      {pestana === 'usuarios' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-slate-700 p-4 pb-3">Usuarios</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {usuarios.map((u) => (
                  <React.Fragment key={u.id}>
                    <tr
                      onClick={() => setUsuarioExpandidoId(usuarioExpandidoId === u.id ? null : u.id)}
                      className="border-t hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <td className="p-3">
                        <p className="font-medium text-slate-700">{u.nombre}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </td>
                      <td className="p-3 text-right text-slate-400 w-10">
                        {usuarioExpandidoId === u.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </td>
                    </tr>
                    {usuarioExpandidoId === u.id && (
                      <tr className="border-t bg-slate-50">
                        <td colSpan={2} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Secciones visibles</p>
                              <div className="space-y-2">
                                {SECCIONES.map((s) => (
                                  <label key={s.key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!u.secciones[s.key]}
                                      disabled={guardando === u.id}
                                      onChange={() => toggleSeccion(u, s.key)}
                                      className="w-4 h-4 accent-primary-600 cursor-pointer"
                                    />
                                    {s.label}
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Acceso por empresa</p>
                              <div className="space-y-3">
                                {empresas.map((e) => {
                                  const rol = u.roles[e.id] || 'sin_acceso';
                                  const modulosEmpresa = MODULOS.filter((m) => e.modulos?.[m.key]);
                                  const ocultos = u.modulosOcultos?.[e.id] || [];
                                  return (
                                    <div key={e.id} className="text-sm">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-slate-600">{e.nombre}</span>
                                        <select
                                          value={rol}
                                          disabled={guardando === u.id}
                                          onChange={(ev) => cambiarRolEmpresa(u, e.id, ev.target.value)}
                                          className="border rounded-md px-2 py-1 text-xs capitalize"
                                        >
                                          {ROLES.map((r) => (
                                            <option key={r} value={r}>{r.replace('_', ' ')}</option>
                                          ))}
                                        </select>
                                      </div>
                                      {rol !== 'sin_acceso' && modulosEmpresa.length > 0 && (
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 pl-3 border-l-2 border-slate-200">
                                          {modulosEmpresa.map((m) => (
                                            <label key={m.key} className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={!ocultos.includes(m.key)}
                                                disabled={guardando === u.id}
                                                onChange={() => toggleModuloUsuario(u, e.id, m.key)}
                                                className="w-3.5 h-3.5 accent-primary-600 cursor-pointer"
                                              />
                                              {m.label}
                                            </label>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-slate-400 mt-2">
                                Debajo de cada empresa, los módulos que ve este usuario ahí. Solo aparecen los que la empresa tiene activos (pestaña Empresas).
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {usuarios.length === 0 && (
                  <tr>
                    <td colSpan={2} className="p-6 text-center text-slate-400">
                      No hay usuarios todavía.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cajeros: PIN de todas las empresas con módulo Caja, juntos */}
      {pestana === 'cajeros' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-slate-700 p-4 pb-3">Cajeros</h2>
          <p className="px-4 pb-3 text-xs text-slate-400">
            El PIN se guarda cifrado y no se puede ver; solo resetear a uno nuevo.
          </p>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Empresa</th>
                <th className="p-3">Nombre</th>
                <th className="p-3 w-24">Estado</th>
                <th className="p-3 w-28 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cajeros.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="p-3 text-slate-600">{c.empresa_nombre}</td>
                  <td className="p-3 font-medium">{c.nombre}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${c.activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => resetearPinCajero(c)}
                      disabled={guardando === `cajero-${c.id}`}
                      title="Resetear PIN"
                      className="text-slate-400 hover:text-primary-600 transition-colors inline-flex items-center gap-1"
                    >
                      <KeyRound size={15} />
                    </button>
                  </td>
                </tr>
              ))}
              {cajeros.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-400">No hay cajeros creados en ninguna empresa con Caja activo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
